import { ActionItem } from '../types';
import { shouldShowAllRecommendations } from '../utils';

/**
 * Service for managing the limit on recommendations per secondary category
 */
export class LimitService {
  private readonly DEFAULT_LIMIT = 4;

  /**
   * Check if results should be limited based on query parameters
   * @returns False if showAllRecommendations=true is in URL, otherwise true
   */
  shouldLimitRecommendationResults(): boolean {
    return !shouldShowAllRecommendations();
  }

  /**
   * Get limited actions - limits per secondary category (area)
   * For example, with limit=2: shows 2 visual, 2 copy, 2 accessibility, 2 performance, 2 seo
   *
   * @param actions - All actions to potentially limit
   * @returns Limited array of actions with max DEFAULT_LIMIT per area
   */
  getLimitedActions(actions: ActionItem[]): ActionItem[] {
    if (!this.shouldLimitRecommendationResults()) {
      return actions;
    }

    // Group actions by secondary category (area)
    const groupedByArea = this.groupByArea(actions);

    // Limit each area to DEFAULT_LIMIT items
    const limitedActions: ActionItem[] = [];
    for (const area in groupedByArea) {
      const areaActions = groupedByArea[area].slice(0, this.DEFAULT_LIMIT);
      limitedActions.push(...areaActions);
    }

    return limitedActions;
  }
  
  /**
   * Group actions by their secondary category (area)
   * @param actions - Actions to group
   * @returns Object with areas as keys and arrays of actions as values
   */
  private groupByArea(actions: ActionItem[]): Record<string, ActionItem[]> {
    const grouped: Record<string, ActionItem[]> = {};

    actions.forEach(action => {
      if (!grouped[action.area]) {
        grouped[action.area] = [];
      }
      grouped[action.area].push(action);
    });

    return grouped;
  }
}
